import { useEffect, useMemo, useState } from "react";
import { CircleNotch, Notebook, Tag } from "@phosphor-icons/react";
import { scanEnex, notebookName } from "@/lib/enexScan";
import { EnexTagList } from "@/components/EnexTagList";
import { EvernoteGuideButton } from "@/components/EvernoteGuide";

// Lets people tick which Evernote notebook files and tags to import, before anything uploads.
export function EnexPicker({ files, onStart, onCancel }) {
  const [scans, setScans] = useState(null);
  const [pct, setPct] = useState(0);
  const [books, setBooks] = useState(() => new Set(files.map((_, i) => i)));
  const [tags, setTags] = useState(null);
  const [untagged, setUntagged] = useState(true);

  useEffect(() => {
    let on = true;
    (async () => {
      const out = [];
      for (let i = 0; i < files.length; i += 1) {
        out.push(await scanEnex(files[i], (p) => on && setPct(Math.round(((i + p / 100) / files.length) * 100))));
      }
      if (!on) return;
      const all = new Set(out.flat(2));
      setScans(out);
      setTags(all);
    })();
    return () => { on = false; };
  }, [files]);

  const counts = useMemo(() => {
    const c = {};
    (scans || []).forEach((notes, i) => books.has(i) && notes.flat().forEach((t) => { c[t] = (c[t] || 0) + 1; }));
    return c;
  }, [scans, books]);

  if (!scans) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 mt-6 flex items-center gap-3" data-testid="enex-scanning">
        <CircleNotch className="animate-spin text-rf-orange" size={20} />
        <span className="text-sm text-slate-700">Looking through your Evernote {files.length > 1 ? "notebooks" : "notebook"} for tags… {pct}%</span>
      </div>
    );
  }
  const allTags = Object.keys(counts).sort((a, b) => a.localeCompare(b));
  const full = books.size === files.length && untagged && allTags.every((t) => tags.has(t));
  const picked = scans.reduce((n, notes, i) => n + (books.has(i) ? notes.filter((t) => (t.length ? t.some((x) => tags.has(x)) : untagged)).length : 0), 0);
  const untaggedCount = scans.reduce((n, notes, i) => n + (books.has(i) ? notes.filter((t) => !t.length).length : 0), 0);
  const toggleBook = (i) => setBooks((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const start = () => onStart(files.filter((_, i) => books.has(i)), full ? null : { tags: [...tags], include_untagged: untagged });

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 mt-6" data-testid="enex-picker">
      <h3 className="font-display text-xl font-semibold text-slate-800">Choose what to bring in</h3>
      <EvernoteGuideButton label="Step-by-step help" testid="guide-open-picker" />
      <p className="text-sm text-slate-600 mt-1" data-testid="enex-picker-hint">
        Everything is ticked, so you'll get a full import. If you'd like less, untick the notebooks or tags you don't want.
      </p>
      <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Notebook size={14} /> Notebooks</p>
      <div className="flex flex-wrap gap-2">
        {files.map((f, i) => (
          <label key={f.name + i} className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
            <input type="checkbox" className="w-4 h-4 accent-rf-orange" checked={books.has(i)} onChange={() => toggleBook(i)} data-testid={`enex-book-${i}`} />
            {notebookName(f)} <span className="text-slate-400">({scans[i].length})</span>
          </label>
        ))}
      </div>
      <p className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Tag size={14} /> Tags</p>
      <EnexTagList allTags={allTags} counts={counts} tags={tags} setTags={setTags} untagged={untagged} setUntagged={setUntagged} untaggedCount={untaggedCount} />
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button type="button" onClick={start} disabled={!picked} data-testid="enex-picker-start"
                className="px-5 py-2 rounded-full bg-rf-orange text-white text-sm font-semibold hover:bg-rf-orange/90 disabled:opacity-50">
          {full ? `Import everything (${picked} notes)` : `Import ${picked} note${picked === 1 ? "" : "s"}`}
        </button>
        <button type="button" onClick={onCancel} data-testid="enex-picker-cancel" className="text-sm font-semibold text-slate-600 hover:text-rf-orange">Cancel</button>
      </div>
    </div>
  );
}
